import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { reportService } from '../services/reportService';
import DeleteConfirmDialog from './ui/DeleteConfirmDialog';
import { invoiceService } from '../services/invoiceService';
import { generateInvoicePDF } from '../services/InvoicePDFGenerator';
import { localStorageUtil } from '../helpers/localStorageUtil';
import { decryptData, encryptData } from '../encryptionUtil'; 
import { useToast } from '../hooks/use-toast';
import { addDays, isPast, format } from 'date-fns';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Mail, Eye, CheckCircle, Undo } from 'lucide-react';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useTheme } from './ui/theme-provider'; // Import useTheme

const db = getFirestore();

const InvoiceDetails = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true); // Only for main data load
  const [emailLoading, setEmailLoading] = useState(false); // For email button only
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false); // Specific state for payment update action
  const [modal, setModal] = useState({ open: false, type: null });
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Get location object
  const { toast } = useToast();
  const { theme } = useTheme(); // Get theme

  // MOVED UP: Helper to safely format dates for the badge
  const formatBadgeDate = useCallback((dateValue) => {
    if (!dateValue) return null;
    try {
      let date;
      if (dateValue.seconds) { // Handle Firestore Timestamp
        date = new Date(dateValue.seconds * 1000);
      } else { // Handle ISO string or Date object
        date = new Date(dateValue);
      }

      if (!isNaN(date.getTime())) {
        return format(date, 'PP'); // e.g., 'Sep 21, 2023'
      } else {
         // console.warn("formatBadgeDate received invalid date value:", dateValue);
      }
    } catch (error) {
      console.error("Error formatting badge date:", dateValue, error);
    }
    return null; // Return null if formatting fails or date is invalid
  }, []); // Added useCallback with empty dependency array as it doesn't depend on component state/props

  // Wrap fetchInvoiceDetails in useCallback for stable reference
  const fetchInvoiceDetails = useCallback(async () => {
    if (!auth.currentUser || !id) {
      console.error('Cannot fetch invoice - missing user or invoice ID');
      setLoading(false); // Ensure loading is off
      setInvoice(null); // Ensure invoice is null
      return;
    }
    console.log(`Fetching details for invoice ID: ${id} (forcing server fetch)`);
    setLoading(true); // Set loading for the fetch operation
    try {
      // Assuming reportService.getInvoiceById fetches FRESH data for the specific ID
      const result = await reportService.getInvoiceById(auth.currentUser.uid, id, { source: 'server' });
      console.log('Fetched invoice details result (from server):', result); // Log the raw result

      // Check if the result is falsy (null, undefined, etc.)
      if (!result) {
        console.warn(`[InvoiceDetails] reportService.getInvoiceById returned no data (result is falsy) for ID: ${id}`);
        setInvoice(null); // Set state to null if no data found
      } else {
        setInvoice(result); // Update state with fresh data
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setInvoice(null); // Clear on error
    } finally {
      setLoading(false); // Ensure loading is false after fetch attempt
      console.log(`Finished fetching details for invoice ID: ${id}`);
    }
  }, [auth.currentUser, id]); // Dependencies for useCallback

  const loadProfileData = useCallback(async () => {
    console.log("[InvoiceDetails] loadProfileData function called.");

    if (auth.currentUser) {
      const userId = auth.currentUser.uid;
      console.log("[InvoiceDetails] auth.currentUser exists:", userId);

      try {
        // *** Fetch latest data from Firestore ***
        console.log("[InvoiceDetails] Fetching latest profile data from Firestore...");
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          console.log("[InvoiceDetails] Fetched userData from Firestore:", userData);

          // *** Update local storage with fresh data ***
          localStorageUtil.setItem(`userData_${userId}`, userData);
          console.log("[InvoiceDetails] Updated local storage with fresh data.");

          // *** Proceed with decryption using the fresh userData ***
          const decryptedData = {
            name: decryptData(userData.name),
            address1: decryptData(userData.address1), // Use address1 key
            address2: decryptData(userData.address2), // Use address2 key
            city: decryptData(userData.city),
            postcode: decryptData(userData.postcode),
            phone: decryptData(userData.phone),
            email: decryptData(userData.email), // Assuming email is also encrypted
            bankName: decryptData(userData.bankName),
            accountNumber: decryptData(userData.accountNumber),
            sortCode: decryptData(userData.sortCode),
            // Add any other fields needed for the PDF that are stored encrypted
          };
          console.log("[InvoiceDetails] Decrypted profile data (using address1/2 keys):", decryptedData);
          setProfileData(decryptedData);

        } else {
          console.error("[InvoiceDetails] User document not found in Firestore for ID:", userId);
          // Clear potentially stale local storage and state
          localStorageUtil.removeItem(`userData_${userId}`);
          setProfileData(null);
          toast({
            title: "Error",
            description: "Could not load profile data. User document missing.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("[InvoiceDetails] Error fetching/processing profile data:", error);
        setProfileData(null); // Clear state on error
        toast({
          title: "Error",
          description: "Failed to load profile data from the server.",
          variant: "destructive",
        });
      }
    } else {
       console.log("[InvoiceDetails] Cannot load profile data, no user logged in.");
       setProfileData(null); // Clear profile data if user logs out
    }
  }, [auth.currentUser, toast]); // Dependency for loadProfileData

  useEffect(() => {
    // *** ADD LOG: Confirm effect execution ***
    console.log("[InvoiceDetails] useEffect for loadProfileData triggered.");
    loadProfileData();
  }, [loadProfileData]); // Use useCallback version

  const handleDeleteInvoice = async () => {
    if (!invoice?.id) return;
    
    try {
      setDeleting(true);
      
      await reportService.deleteInvoice(invoice.id);
      
      toast({
        title: "Success",
        description: "Invoice deleted successfully",
        variant: "success",
      });
      
      setShowDeleteDialog(false);
      
      // Navigate back with the deleted invoice ID in state
      navigate('/invoice-history', { 
        state: { deletedInvoiceId: invoice.id }
      });
    } catch (error) {
      console.error("Error deleting invoice:", error); // Keep essential error logs
      toast({
        title: "Error",
        description: "Failed to delete invoice. Please try again.",
        variant: "destructive",
      });
      setDeleting(false);
    }
  };

  const handleRestoreInvoice = async () => {
    if (!invoice?.id) return;
    
    try {
      setRestoring(true);
      
      await reportService.restoreInvoice(invoice.id);
      
      toast({
        title: "Success",
        description: "Invoice restored successfully",
        variant: "success",
      });
      
      setShowDeleteDialog(false);
      
      // Navigate back with the restored invoice ID in state
      navigate('/invoice-history', { 
        state: { restoredInvoiceId: invoice.id }
      });
    } catch (error) {
      console.error("Error restoring invoice:", error); // Keep essential error logs
      toast({
        title: "Error",
        description: "Failed to restore invoice. Please try again.",
        variant: "destructive",
      });
      setRestoring(false);
    }
  };

  // Updated handlePaymentChange - Stores update info in sessionStorage
  const handlePaymentChange = async (invoiceId, checked) => {
    if (!auth.currentUser || !invoiceId) return;
    setIsMarkingPaid(true); // Indicate button action is happening
    // setLoading(true); // No longer setting general loading here
    console.log(`Attempting to mark invoice ${invoiceId} as ${checked ? 'paid' : 'unpaid'}`);
    try {
      let updatedFields; // Variable to store the returned updated fields

      if (checked) {
        updatedFields = await invoiceService.markInvoicePaid(auth.currentUser.uid, invoiceId);
        toast({ title: "Success", description: "Invoice marked as paid.", variant: "success" });
        console.log(`Successfully marked ${invoiceId} as paid.`);
      } else {
        updatedFields = await invoiceService.markInvoiceUnpaid(auth.currentUser.uid, invoiceId);
        toast({ title: "Success", description: "Invoice marked as unpaid.", variant: "success" });
        console.log(`Successfully marked ${invoiceId} as unpaid.`);
      }

      // **Direct State Update:** Update local state using the confirmed data from the service
      if (updatedFields) {
        setInvoice(prev => prev ? { ...prev, ...updatedFields } : null);
        console.log(`Directly updated local state for ${invoiceId} with:`, updatedFields);

        // **Store update info for InvoiceHistory**
        try {
          const updateDataForStorage = {
            id: invoiceId,
            isPaid: updatedFields.isPaid,
            paidDate: updatedFields.paidDate ? updatedFields.paidDate.toISOString() : null // Store date as ISO string
          };
          // *** ADD LOGGING HERE ***
          console.log('[InvoiceDetails] Storing update info in sessionStorage:', updateDataForStorage);
          sessionStorage.setItem('invoiceUpdateInfo', JSON.stringify(updateDataForStorage));
          console.log(`Stored update info for ${invoiceId} in sessionStorage.`);
        } catch (e) {
          console.error("Failed to write to sessionStorage:", e);
        }

      } else {
         // Fallback: If service didn't return data, trigger a fetch (shouldn't happen with new service code)
         console.warn(`Service did not return updated fields for ${invoiceId}, triggering manual fetch.`);
         await fetchInvoiceDetails();
      }

      // REMOVED re-fetch call from here:
      // await fetchInvoiceDetails();

    } catch (error) {
      console.error(`Error marking invoice as ${checked ? 'paid' : 'unpaid'}:`, error);
      toast({ title: "Error", description: `Failed to mark invoice as ${checked ? 'paid' : 'unpaid'}.`, variant: "destructive" });
      // setLoading(false); // Ensure loading is turned off on error
    } finally {
      setIsMarkingPaid(false); // Action finished
      console.log(`Finished payment change process for ${invoiceId}`);
    }
  };

  const getPaymentStatusStyle = (invoice) => {
    if (!invoice) return { text: 'Unknown', className: cn(theme === 'light' ? 'border-gray-300 text-gray-600 bg-gray-100' : 'border-gray-500/50 text-gray-300 bg-gray-700/20') };
     if (invoice.status === 'deleted') {
      return { text: 'Deleted', className: cn(theme === 'light' ? 'border-red-300 text-red-700 bg-red-100' : 'border-red-500/50 text-red-400 bg-red-900/20') };
    }
    if (invoice.isPaid) {
       // Use the safe formatter here (now defined above)
       const paidDateFormatted = formatBadgeDate(invoice.paidDate);
       // Construct text conditionally based on successful formatting
       const text = paidDateFormatted ? `Paid ${paidDateFormatted}` : 'Paid';
       // Adjusted light mode for paid badge
       return { text: text, className: cn(theme === 'light' ? 'border-green-300 text-green-900 bg-green-100' : 'border-green-500/50 text-green-300 bg-green-900/20'), date: invoice.paidDate };
    }

    // *** ADJUST createdAtDate INITIALIZATION ***
    let createdAtDate = null;
    if (invoice.createdAt?.seconds) { // Check for Firestore Timestamp first
        createdAtDate = new Date(invoice.createdAt.seconds * 1000);
    } else if (invoice.createdAt instanceof Date) { // Check if it's already a JS Date
        createdAtDate = invoice.createdAt;
    } else if (invoice.createdAt) { // Fallback: Try creating a Date from whatever it is (e.g., ISO string)
        try {
            const parsedDate = new Date(invoice.createdAt);
            if (!isNaN(parsedDate.getTime())) {
                createdAtDate = parsedDate;
            }
        } catch (e) {
             console.warn("Could not parse invoice.createdAt:", invoice.createdAt, e);
        }
    }

    // Check if createdAtDate is valid before proceeding
    if (!createdAtDate || isNaN(createdAtDate.getTime())) {
      console.warn("Invalid or missing createdAt for due date calculation:", invoice.createdAt); // Keep warn
      // Adjusted light mode for unpaid badge
      return { text: 'Unpaid', className: cn(theme === 'light' ? 'border-yellow-300 text-yellow-800 bg-yellow-100' : 'border-yellow-500/50 text-yellow-300 bg-yellow-900/20') };
    }

    const dueDate = addDays(createdAtDate, 30);
    if (isPast(dueDate)) {
      // Add isOverdue flag
      // Adjusted light mode for overdue badge
      return { text: 'Overdue', className: cn(theme === 'light' ? 'border-red-300 text-red-800 bg-red-100' : 'border-red-500/50 text-red-300 bg-red-900/20'), dueDate, isOverdue: true };
    }
    // Adjusted light mode for unpaid badge
    return { text: 'Unpaid', className: cn(theme === 'light' ? 'border-yellow-300 text-yellow-800 bg-yellow-100' : 'border-yellow-500/50 text-yellow-300 bg-yellow-900/20'), dueDate };
  };

  const formatRateValue = (value, type, isRateColumn = false) => {
    const numValue = parseFloat(value).toFixed(2);
    if (type === 'percentage') {
      // For percentage invoices, the "Rate" in the row is the patient charge rate (currency)
      // Only show % when displaying the actual percentage rate (not in table rows)
      if (isRateColumn) {
        // In the table row, show patient charge rate as currency
        return `£${numValue}`;
      }
      // For header/summary display, show as percentage
      return `${numValue}%`;
    }
    if (type === 'hourly') {
      return `£${numValue}/hr`;
    }
    if (type === 'daily') {
      return `£${numValue}/day`;
    }
    // For 'fixed' or any other type
    return `£${numValue}`;
  };

  const getDescriptionFromPatients = (entry) => {
    if (!entry.patients || !Array.isArray(entry.patients)) return 'N/A';
    const descriptions = entry.patients.map(p => p.description).filter(Boolean);
    return descriptions.length > 0 ? descriptions.join(', ') : 'Daily List';
  };

  // Build detailed description for percentage invoices showing all patient types
  const buildPercentageDescription = (patients) => {
    if (!patients || !Array.isArray(patients) || patients.length === 0) return 'N/A';
    return patients.map(p => {
      const count = parseInt(p.count) || 0;
      const rate = parseFloat(p.rate) || 0;
      const desc = p.description || '';
      const pluralText = count !== 1 ? 's' : '';
      return `${count} patient${pluralText} @ £${rate.toFixed(2)}${desc ? ` (${desc})` : ''}`;
    }).join(' + ');
  };

  // Calculate patient collections (without percentage) for a single entry
  const calculatePatientCollections = (patients) => {
    if (!patients || !Array.isArray(patients)) return 0;
    return patients.reduce((sum, p) => {
      const count = parseInt(p.count) || 0;
      const rate = parseFloat(p.rate) || 0;
      return sum + (count * rate);
    }, 0);
  };

  const formatRow = (item) => {
    if (!invoice) return {}; // Handle case where invoice is not loaded yet

    // Helper to format dates within formatRow, checking validity
    const formatDateSafely = (dateValue) => {
      if (!dateValue) return 'N/A';
      try {
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return format(date, 'PP');
        }
      } catch (e) {
        console.error("Error formatting date in formatRow:", dateValue, e);
      }
      return 'Invalid Date';
    };

    if (invoice.rateType === 'hourly') {
      return {
        date: formatDateSafely(item.date),
        description: item.patients?.[0]?.description || 'Hours worked',
        count: item.patients?.[0]?.count || 0,
        rate: invoice.rateValue,
        amount: item.amount
      };
    } else if (invoice.rateType === 'percentage') {
      // For percentage invoices: show patient collections (not percentage amount)
      const totalPatientCount = item.patients?.reduce((sum, p) => sum + (parseInt(p.count) || 0), 0) || 0;
      const patientCollections = calculatePatientCollections(item.patients);
      return {
        date: formatDateSafely(item.date),
        description: buildPercentageDescription(item.patients),
        count: totalPatientCount,
        rate: '-', // Rate varies per patient type, show in description
        amount: patientCollections // Show collections, not percentage amount
      };
    } else {
      // For per_patient and fixed
      const totalPatientCount = item.patients?.reduce((sum, p) => sum + (parseInt(p.count) || 0), 0) || 0;
      return {
        date: formatDateSafely(item.date),
        description: getDescriptionFromPatients(item),
        count: totalPatientCount,
        rate: item.patients?.[0]?.rate || 0,
        amount: item.amount
      };
    }
  };

  const formatInvoiceDescription = (item) => {
    if (invoice.rateType === 'hourly') {
      return {
        count: item.patients[0].count,
        rate: invoice.rateValue,
        description: item.patients[0].description || 'Hours worked'
      };
    } else {
      return {
        count: item.totalPatients,
        rate: item.patients[0].rate,
        description: getDescriptionFromPatients(item)
      };
    }
  };

  const formatEntriesForPDF = (summaryData, rateType, rateValue) => {
    // 1. Group entries by date
    const groupedByDate = summaryData.reduce((acc, item) => {
      let dateKey;
      if (item.date?.seconds) {
        dateKey = new Date(item.date.seconds * 1000).toISOString().split('T')[0]; // Use YYYY-MM-DD as key
      } else if (item.date) {
        try {
          dateKey = new Date(item.date).toISOString().split('T')[0];
        } catch (e) {
          dateKey = 'invalid-date';
        }
      } else {
        dateKey = 'no-date';
      }

      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: item.date?.seconds ? new Date(item.date.seconds * 1000) : new Date(item.date || Date.now()),
          items: []
        };
      }
      acc[dateKey].items.push(item);
      return acc;
    }, {});

    // 2. Process each date group to combine descriptions and calculate totals
    const formattedEntries = Object.values(groupedByDate).map(group => {
      const formattedDate = group.date.toLocaleDateString(); // Or use format() from date-fns
      let combinedDescription = '';
      let dateTotalAmount = 0;
      const descriptionParts = [];

      group.items.forEach(item => {
        dateTotalAmount += parseFloat(item.amount || 0); // Sum amounts for the date

        // Build description part for this specific item
        if (rateType === 'hourly') {
          const hours = item.patients?.[0]?.count || 0; // Assuming count is hours here
          const desc = item.patients?.[0]?.description || 'Hours worked';
          descriptionParts.push(`${hours} hours × £${parseFloat(rateValue).toFixed(2)}${desc !== 'Hours worked' ? ` (${desc})` : ''}`);
        } else if (rateType === 'percentage' && item.patients?.length > 0) {
          item.patients.forEach(patient => {
            const count = parseInt(patient.count) || 0;
            const rate = parseFloat(patient.rate) || 0;
            const percentValue = parseFloat(rateValue) || 0;
            const desc = patient.description || '';
            descriptionParts.push(`${count} × £${rate.toFixed(2)} @ ${percentValue}%${desc ? ` (${desc})` : ''}`);
          });
        } else if (rateType === 'fixed') {
           const desc = item.patients?.[0]?.description || 'Fixed rate work';
           descriptionParts.push(`Fixed rate: £${parseFloat(rateValue).toFixed(2)}${desc !== 'Fixed rate work' ? ` (${desc})` : ''}`);
        } else {
          descriptionParts.push(item.description || 'Invoice item'); // Fallback
        }
      });

      combinedDescription = descriptionParts.join(' | '); // Join descriptions for the date

      return {
        date: group.date,
        formattedDate: formattedDate,
        description: combinedDescription,
        amount: dateTotalAmount,
        // Keep original items if needed for detailed breakdown later, otherwise remove
        // originalItems: group.items
      };
    });

    // Sort by date if necessary
    formattedEntries.sort((a, b) => a.date - b.date);

    console.log("[InvoiceDetails] Formatted Entries for PDF (Grouped by Date):", formattedEntries); // Log the final structure
    return formattedEntries;
  };

  // Helper to robustly resolve practice object (matches InvoiceActions logic)
  const resolvePracticeForPDF = async () => {
    let practice = invoice.practiceDetails;
    // If practiceDetails is a string (code/id), try to resolve
    if (typeof practice === 'string') {
      try {
        // Try to import helpers only when needed
        const { practiceDataUtil } = await import('../helpers/practiceDataUtil');
        const { practiceService } = await import('../services/practiceService');
        let found = await practiceDataUtil.getPracticeByCode(practice);
        if (found) return found;
        found = await practiceService.getPracticeById(practice, null, invoice.userId);
        if (found) return found;
      } catch (e) {
        console.warn('Failed to resolve practice by code/id:', e);
      }
    }
    return practice;
  };


  // PDF Download Confirmation Modal
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const handleGeneratePDF = async () => {
    if (!invoice || !profileData) {
      console.error("Cannot generate PDF: Missing invoice or profile data.");
      return;
    }

    const practice = await resolvePracticeForPDF();
    console.log("[InvoiceDetails] Data being passed to PDF Generator:");
    console.log("Invoice Data:", invoice);
    console.log("Profile Data for PDF:", profileData);
    console.log("Practice for PDF:", practice);

    const pdfData = {
      practice,
      entries: invoice.summaryData, // Pass raw summaryData - PDF generator handles formatting
      total: invoice.totalAmount,
      invoiceNumber: invoice.invoiceNumber,
      profile: profileData,
      rateType: invoice.rateType,
      rateValue: invoice.rateValue
    };

    // Modern generator returns { doc, filename }
    const { doc, filename } = await generateInvoicePDF(pdfData);
    doc.save(filename); // <-- This triggers the download
    setPdfModalOpen(true); // Show confirmation modal
  };

  const handleEmailReminder = (inv) => {
    if (!inv) return;
    const practiceName = inv.practiceDetails?.prac_name || 'the practice';
    const userName = auth.currentUser?.displayName || "[Your Name]";
    const subject = `Invoice Reminder: ${inv.invoiceNumber}`;
    const body = `Hi ${practiceName},\n\nThis is a friendly reminder that invoice ${inv.invoiceNumber} for £${parseFloat(inv.totalAmount || 0).toFixed(2)} is overdue.\n\nPlease arrange payment at your earliest convenience.\n\nThanks,\n${userName}`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  // Updated: Use same logic as InvoiceActions.jsx for emailing PDF to self
  const handleEmailToSelf = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!invoice || !profileData) {
      toast({
        title: "Error",
        description: "Invoice details are not available.",
        variant: "destructive",
      });
      return;
    }

    try {
      setEmailLoading(true);
      // Dynamically import Firebase functions (to avoid circular deps)
      const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
      // Import PDF generator (should match InvoiceActions)
      const { generateInvoicePDFBlob } = await import('../services/InvoicePDFGenerator');

      // Robustly resolve practice (matches InvoiceActions)
      const practice = await resolvePracticeForPDF();

      // Prepare PDF data (match InvoiceActions logic)
      const pdfBlob = await generateInvoicePDFBlob({
        practice,
        entries: invoice.summaryData,
        total: invoice.totalAmount,
        invoiceNumber: invoice.invoiceNumber,
        profile: profileData,
        rateType: invoice.rateType,
        rateValue: invoice.rateValue
      });
      // Convert blob to base64
      const blobToBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const pdfBase64 = await blobToBase64(pdfBlob);
      const userEmail = profileData?.email;
      const practiceName = practice?.prac_name?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
      const filename = `${practiceName}_invoice_${invoice.invoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
      const subject = `Invoice ${invoice.invoiceNumber} from ${profileData?.name || 'Dental Hygienist'}`;
      const fullName = profileData?.name || 'Dental Hygienist';
      const html = `Please find your invoice attached.<br><br>Kind regards,<br>${fullName}`;
      const plainText = `Please find your invoice attached.\n\nKind regards,\n${fullName}`;
      const functions = getFunctions();
      if (window.location.hostname === 'localhost') {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      const sendEmailWithAttachment = httpsCallable(functions, 'sendEmailWithAttachment');
      const result = await sendEmailWithAttachment({
        to: userEmail,
        subject,
        html,
        plainText,
        filename,
        pdfBase64,
        name: fullName
      });
      if (result.data && result.data.success) {
        setModal({ open: true, type: 'emailSuccess' });
      } else {
        setModal({ open: true, type: 'emailError' });
      }
    } catch (error) {
      console.error("Error emailing invoice:", error);
      setModal({ open: true, type: 'emailError' });
    } finally {
      setEmailLoading(false);
    }
  };

  // Effect for initial data load and when ID changes
  useEffect(() => {
    // *** Verify that reportService is correctly imported and contains getInvoiceById ***
    // *** The error "reportService.getInvoiceById is not a function" originates here. ***
    // *** Check src/services/reportService.js export structure. ***
    console.log('[InvoiceDetails] ID change or initial load effect triggered.');
    fetchInvoiceDetails(); // Fetch details when component mounts or ID changes
    // loadProfileData is handled by its own useEffect now
  }, [fetchInvoiceDetails]); // Depend only on the memoized fetch function (which depends on id)

  // Adjusted base classes for theme
  const badgeBaseClasses = cn(
    "inline-block px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-semibold border whitespace-nowrap",
    theme === 'light' ? 'border-gray-300' : 'border-transparent' // Example: light border, no border in dark
  );

  const paymentStatus = invoice ? getPaymentStatusStyle(invoice) : null;
  const isOverdue = paymentStatus?.isOverdue || false;
  const formattedDueDateForBadge = paymentStatus?.dueDate ? formatBadgeDate(paymentStatus.dueDate) : null;

  // Show loading indicator if loading state is true
  if (loading) {
    return (
      <div className="container mx-auto p-4 text-center">
        {/* Loading text: Theme-aware */}
        <span className={cn(theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Loading invoice details...</span>
      </div>
    );
  }

  // *** ADD LOGGING HERE ***
  console.log('[InvoiceDetails] Rendering with invoice state:', invoice);

  // Helper function to render the formatted date or 'N/A'
  const renderInvoiceDate = (dateValue) => {
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      try {
        return format(dateValue, 'PP'); // e.g., 'Sep 21, 2023'
      } catch (e) {
        console.error("Error formatting invoice.createdAt:", dateValue, e);
        return 'Invalid Date';
      }
    }
    console.warn("[InvoiceDetails] invoice.createdAt is not a valid Date object:", dateValue);
    return 'N/A';
  };

  return (
    <div className="container mx-auto p-2 sm:p-4">
      {/* Card: Theme-aware */}
      <Card className={cn(theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E]', theme === 'light' ? 'border-gray-200' : 'border-purple-500/20')}>
        <CardContent className="p-3 sm:p-6">
          {/* Header: Modernized layout with title and non-overlapping badge */}
          <div className="relative mb-4 sm:mb-6">
            <div className="flex flex-row justify-between items-start">
              {/* Title */}
              <h2 className={cn(
                "text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-0",
                theme === 'light' ? 'text-black' : 'text-purple-200'
              )}>
                Invoice Details
              </h2>
              {/* Badge in top right, not overlapping */}
              {paymentStatus && (
                <span className={cn(
                  badgeBaseClasses,
                  paymentStatus.className,
                  "ml-4 text-xs sm:text-sm"
                )}>
                  {paymentStatus.text}
                  {(paymentStatus.text === 'Overdue' || paymentStatus.text === 'Unpaid') && formattedDueDateForBadge && (
                    <span className={cn(
                      "ml-1 sm:ml-1.5 text-xs",
                      theme === 'light' ? 'opacity-70' : 'opacity-80'
                    )}>(Due: {formattedDueDateForBadge})</span>
                  )}
                </span>
              )}
            </div>
            {/* Invoice number and date on one line */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 mt-1">
              <div className="flex flex-row items-baseline gap-2 flex-wrap w-full">
                {/* Invoice # and Date: Invoice # left, Date right */}
                <div className="flex-1 min-w-0 flex flex-row items-baseline gap-2">
                  <span className={cn("text-sm font-medium uppercase tracking-wide", theme === 'light' ? 'text-gray-500' : 'text-purple-200')}>Invoice #</span>
                  <span className={cn("text-sm font-normal", theme === 'light' ? 'text-gray-900' : 'text-white')}>{invoice.invoiceNumber}</span>
                </div>
                <div className="flex-1 min-w-0 flex flex-row items-baseline gap-2 justify-end text-right">
                  <span className={cn("text-sm font-medium uppercase tracking-wide", theme === 'light' ? 'text-gray-500' : 'text-purple-200')}>Date</span>
                  <span className={cn("text-sm font-normal", theme === 'light' ? 'text-gray-900' : 'text-white')}>{renderInvoiceDate(invoice.createdAt)}</span>
                </div>
              </div>
            </div>
            {/* Practice name on its own line */}
            <div className="mt-2">
              <span className={cn("text-sm font-medium uppercase tracking-wide", theme === 'light' ? 'text-gray-500' : 'text-purple-200')}>Practice</span>
              <div className={cn("truncate text-sm font-normal mt-0.5", theme === 'light' ? 'text-gray-800' : 'text-purple-100')}>{invoice.practiceDetails?.prac_name || 'N/A'}</div>
            </div>
            {/* Amount and Rate on one line: Amount left, Rate right */}
            <div className="flex flex-row items-baseline gap-6 mt-2 flex-wrap w-full">
              <div className="flex-1 min-w-0 flex flex-row items-baseline gap-2">
                <span className={cn("text-sm font-medium uppercase tracking-wide", theme === 'light' ? 'text-gray-500' : 'text-purple-200')}>Amount</span>
                <span className={cn("text-sm font-normal", theme === 'light' ? 'text-black' : 'text-white')}>£{invoice.totalAmount ? parseFloat(invoice.totalAmount).toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex-1 min-w-0 flex flex-row items-baseline gap-2 justify-end text-right">
                <span className={cn("text-sm font-medium uppercase tracking-wide", theme === 'light' ? 'text-gray-500' : 'text-purple-200')}>Rate</span>
                <span className={cn("text-sm font-normal", theme === 'light' ? 'text-gray-800' : 'text-purple-100')}>{formatRateValue(invoice.rateValue, invoice.rateType)}</span>
              </div>
            </div>
          </div>
          {/* PDF Download Confirmation Modal */}
          <Dialog open={pdfModalOpen} onOpenChange={setPdfModalOpen}>
            <DialogContent className={cn(
              theme === 'light'
                ? 'bg-white text-gray-800 shadow-lg rounded-lg'
                : 'bg-[#23233a] text-gray-200 shadow-md rounded-md border border-purple-700/40'
            )}>
              <DialogHeader>
                <DialogTitle className={cn(
                  'text-lg font-semibold',
                  theme === 'light' ? 'text-purple-700' : 'text-purple-300'
                )}>PDF Downloaded</DialogTitle>
              </DialogHeader>
              <div className="py-4 text-sm leading-relaxed">
                Your invoice PDF has been downloaded.<br />
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Check your browser's downloads folder for the file.</li>
                  <li>You can now print, email, or share the PDF as needed.</li>
                </ul>
              </div>
              <DialogFooter className="pt-4 border-t mt-4">
                <Button
                  onClick={() => setPdfModalOpen(false)}
                  variant="outline"
                  className={cn(
                    'w-full',
                    theme === 'light'
                      ? 'text-white bg-purple-700 hover:bg-purple-800 border-purple-700'
                      : 'text-white bg-purple-500 hover:bg-purple-400 border-purple-500'
                  )}
                >OK</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Conditional rendering based on invoice existence */}
          {invoice ? (
            <div>
              {/* Deleted Message: Theme-aware */}
              {invoice.status === 'deleted' && (
                <div className={cn(
                  "mb-4 p-2 rounded-lg border",
                  theme === 'light'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-red-500/10 border-red-500/20'
                )}>
                  {/* Text: Theme-aware */}
                  <p className={cn(theme === 'light' ? 'text-red-700' : 'text-red-400')}>This invoice has been deleted</p>
                  <p className={cn(
                    "text-sm",
                    theme === 'light' ? 'text-red-600/80' : 'text-red-400/80'
                  )}>
                    Deleted on: {invoice.deletedAt?.seconds ? new Date(invoice.deletedAt.seconds * 1000).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              )}
          {/* Invoice Info: Removed duplicate block, now only modernized header is shown above */}

              {/* Invoice Items Table: Theme-aware */}
              <div className="mt-4 sm:mt-6 overflow-x-auto -mx-3 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden">
                    {/* Table: Theme-aware dividers */}
                    <table className={cn(
                      "min-w-full divide-y",
                      theme === 'light' ? 'divide-gray-200' : 'divide-purple-500/20'
                    )}>
                      {/* Header: Theme-aware background and text */}
                      <thead className={cn(theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]')}>
                        <tr>
                          <th className={cn("py-2 px-2 sm:px-4 text-left text-xs sm:text-sm font-semibold", invoice.rateType === 'percentage' ? "w-[15%]" : "w-[20%]", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Date</th>
                          <th className={cn("py-2 px-2 sm:px-4 text-left text-xs sm:text-sm font-semibold", invoice.rateType === 'percentage' ? "w-[55%]" : "w-[40%]", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Description</th>
                          <th className={cn("py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-semibold w-[10%]", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>
                            {invoice.rateType === 'hourly' ? 'Hours' : 'Patients'}
                          </th>
                          {invoice.rateType !== 'percentage' && (
                            <th className={cn("py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-semibold w-[13%]", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Rate</th>
                          )}
                          <th className={cn("py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-semibold", invoice.rateType === 'percentage' ? "w-[20%]" : "w-[14%]", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>
                            Amount
                          </th>
                        </tr>
                      </thead>
                      {/* Body: Theme-aware dividers and text */}
                      <tbody className={cn(
                        "divide-y",
                        theme === 'light' ? 'divide-gray-200' : 'divide-purple-500/20',
                        theme === 'light' ? 'text-gray-800' : 'text-white' // Default text color for body
                      )}>
                        {invoice.summaryData?.map((item, index) => {
                          // Check if this is a special row (subtotal, percentage, or total)
                          const isSpecialRow = item.isSubtotal || item.isPercentage || item.isTotal;
                          
                          if (isSpecialRow) {
                            // Render special rows differently - parse value correctly
                            let amount = 0;
                            if (item.value) {
                              // Handle both "£123.45" format and "description = £123.45" format
                              const valueStr = item.value.includes('=') 
                                ? item.value.split('=')[1]?.trim() 
                                : item.value;
                              amount = parseFloat(valueStr?.replace('£', '').trim()) || 0;
                            }
                            const specialColSpan = invoice.rateType === 'percentage' ? 3 : 4;
                            return (
                              <tr 
                                key={index} 
                                className={cn(
                                  item.isSubtotal ? "border-t-2" : "",
                                  theme === 'light'
                                    ? 'bg-gray-100 border-purple-600'
                                    : 'bg-[#2A2A3E] border-purple-500'
                                )}
                              >
                                <td className={cn("py-2 px-2 sm:px-4 text-left text-xs sm:text-sm", item.isTotal ? "font-bold" : "font-semibold")} colSpan={specialColSpan}>
                                  {item.label}
                                </td>
                                <td className={cn("py-2 px-2 sm:px-4 text-right text-xs sm:text-sm", item.isTotal ? "font-bold" : "font-semibold")}>
                                  £{amount.toFixed(2)}
                                </td>
                              </tr>
                            );
                          }
                          
                          // Regular rows
                          const rowData = formatRow(item);
                          const colSpanForSummary = invoice.rateType === 'percentage' ? 3 : 4;
                          return (
                            // Row: Theme-aware alternating background
                            <tr key={index} className={cn(
                              theme === 'light'
                                ? (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')
                                : (index % 2 === 0 ? 'bg-[#1F1F2E]' : 'bg-[#2A2A3E]')
                            )}>
                              {/* Cells: Theme-aware text (inherits from tbody) */}
                              <td className="py-2 px-2 sm:px-4 text-left text-xs sm:text-sm truncate">{rowData.date}</td>
                              <td className="py-2 px-2 sm:px-4 text-left text-xs sm:text-sm">{rowData.description}</td>
                              <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm">{rowData.count}</td>
                              {invoice.rateType !== 'percentage' && (
                                <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm">
                                  {formatRateValue(rowData.rate, invoice.rateType, true)}
                                </td>
                              )}
                              <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm">£{parseFloat(rowData.amount || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                        
                        {/* For percentage invoices without special rows, add summary rows dynamically */}
                        {invoice.rateType === 'percentage' && !invoice.summaryData?.some(item => item.isSubtotal || item.isTotal) && (() => {
                          // Calculate totals from summaryData
                          const totalCollections = invoice.summaryData?.reduce((sum, item) => {
                            if (item.isSubtotal || item.isPercentage || item.isTotal) return sum;
                            return sum + calculatePatientCollections(item.patients);
                          }, 0) || 0;
                          const percentageRate = parseFloat(invoice.rateValue) || 0;
                          const commissionAmount = totalCollections * (percentageRate / 100);
                          
                          return (
                            <>
                              <tr className={cn(
                                "border-t-2",
                                theme === 'light' ? 'bg-gray-100 border-purple-600' : 'bg-[#2A2A3E] border-purple-500'
                              )}>
                                <td className="py-2 px-2 sm:px-4 text-left text-xs sm:text-sm font-semibold" colSpan={3}>
                                  Total Charged to Patients
                                </td>
                                <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-semibold">
                                  £{totalCollections.toFixed(2)}
                                </td>
                              </tr>
                              <tr className={cn(
                                theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]'
                              )}>
                                <td className="py-2 px-2 sm:px-4 text-left text-xs sm:text-sm font-semibold" colSpan={3}>
                                  Commission ({percentageRate}%)
                                </td>
                                <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-semibold">
                                  £{commissionAmount.toFixed(2)}
                                </td>
                              </tr>
                              <tr className={cn(
                                theme === 'light' ? 'bg-gray-200' : 'bg-[#3A3A4E]'
                              )}>
                                <td className="py-2 px-2 sm:px-4 text-left text-xs sm:text-sm font-bold" colSpan={3}>
                                  Invoice Total
                                </td>
                                <td className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm font-bold">
                                  £{commissionAmount.toFixed(2)}
                                </td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Divider: Theme-aware, thin line above action buttons for visual separation */}
              <div
                className={cn(
                  'w-full my-6',
                  theme === 'light'
                    ? 'border-t border-gray-200'
                    : 'border-t border-purple-500/30'
                )}
                aria-hidden="true"
              />
              {/* Action Buttons: Two-line, two-column layout with icons, consistent spacing */}
              <div className="flex flex-col gap-2">
                {/* First line: Main actions, two columns always */}
                <div className="grid grid-cols-2 gap-2 w-full">
                  {invoice.status === 'deleted' ? (
                    <Button
                      onClick={handleRestoreInvoice}
                      variant="default"
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white w-full"
                      disabled={restoring || loading}
                    >
                      <Undo className="h-5 w-5 mr-2" />
                      {restoring ? 'Restoring...' : 'Restore'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => handlePaymentChange(invoice.id, !invoice.isPaid)}
                        variant="default"
                        size="lg"
                        className={cn(
                          invoice.isPaid
                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white',
                          'w-full flex items-center justify-center'
                        )}
                        disabled={isMarkingPaid || deleting || loading}
                      >
                        {invoice.isPaid ? <Undo className="h-5 w-5 mr-2" /> : <CheckCircle className="h-5 w-5 mr-2" />}
                        {isMarkingPaid ? 'Updating...' : invoice.isPaid ? 'Mark Unpaid' : 'Mark Paid'}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleEmailToSelf}
                        variant="default"
                        size="lg"
                        className="bg-purple-600 hover:bg-purple-700 text-white w-full flex items-center justify-center"
                        disabled={deleting || isMarkingPaid || loading || emailLoading}
                        title="Email this invoice to yourself"
                      >
                        <Mail className="h-5 w-5 mr-2" />
                        {emailLoading ? 'Emailing...' : 'Email Self'}
                      </Button>
                    </>
                  )}
                </div>
                {/* Second line: PDF and Delete, two columns always */}
                <div className="grid grid-cols-2 gap-2 w-full">
                  <Button
                    onClick={handleGeneratePDF}
                    variant="outline"
                    size="lg"
                    className={cn(
                      'w-full flex items-center justify-center',
                      theme === 'light'
                        ? 'border-purple-700 text-purple-700 hover:bg-purple-50'
                        : 'border-purple-400 text-purple-200 hover:bg-purple-900/30 hover:text-purple-100'
                    )}
                    disabled={deleting || isMarkingPaid || loading}
                  >
                    <span className="mr-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" /></svg></span>
                    PDF
                  </Button>
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    variant="destructive"
                    size="lg"
                    className={cn(
                      'w-full flex items-center justify-center border font-semibold group',
                      theme === 'light'
                        ? 'border-red-700 text-red-700 hover:bg-red-700'
                        : 'border-red-500 text-red-300 hover:bg-red-600'
                    )}
                    disabled={deleting || isMarkingPaid || loading}
                    aria-label="Delete Invoice"
                  >
                    <span className="mr-2 flex items-center">
                      <RiDeleteBinLine className={cn(
                        'h-5 w-5',
                        theme === 'light' ? 'text-red-700 group-hover:text-white' : 'text-red-300 group-hover:text-white'
                      )} />
                    </span>
                    {deleting ? 'Deleting...' : <span className={cn(
                      theme === 'light' ? 'text-red-700 group-hover:text-white' : 'text-red-300 group-hover:text-white'
                    )}>Delete</span>}
                  </Button>
                </div>
                {/* Third line: Back button, full width */}
                <div className="flex flex-row justify-center sm:justify-end mt-2">
                  <Button
                    onClick={() => navigate(-1)}
                    variant="default"
                    size="lg"
                    className={cn(
                      'w-full sm:w-auto',
                      'bg-purple-600 text-white hover:bg-purple-700'
                    )}
                    disabled={loading || isMarkingPaid || deleting || restoring}
                  >
                    <span className="mr-2">&#8592;</span> Back
                  </Button>
                </div>
                {/* Email Success/Error Modal (unchanged) */}
                <Dialog open={modal.open} onOpenChange={open => setModal(m => ({ ...m, open }))}>
                  {modal.type === 'emailSuccess' && (
                    <DialogContent className={cn(
                      theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
                    )}>
                      <DialogHeader>
                        <DialogTitle className={cn(theme === 'light' ? 'text-green-700' : 'text-green-300')}>Invoice Sent!</DialogTitle>
                      </DialogHeader>
                      <div className="py-4 text-sm leading-relaxed">
                        Your invoice PDF has been emailed to you.<br />
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Check your inbox (and spam folder).</li>
                          <li>You can forward the email or download the PDF from your email app.</li>
                        </ul>
                      </div>
                      <DialogFooter className="pt-4 border-t mt-4">
                        <Button onClick={() => setModal({ open: false, type: null })} variant="outline" className={cn(
                          theme === 'light' ? 'text-white bg-green-600 hover:bg-green-700 border-green-600' : 'text-white bg-green-500 hover:bg-green-600 border-green-500'
                        )}>OK</Button>
                      </DialogFooter>
                    </DialogContent>
                  )}
                  {modal.type === 'emailError' && (
                    <DialogContent className={cn(
                      theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
                    )}>
                      <DialogHeader>
                        <DialogTitle className={cn(theme === 'light' ? 'text-red-700' : 'text-red-300')}>Email Failed</DialogTitle>
                      </DialogHeader>
                      <div className="py-4 text-sm leading-relaxed">
                        Sorry, we couldn't send your invoice email.<br />
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Check your internet connection and try again.</li>
                          <li>If the problem persists, contact support.</li>
                        </ul>
                      </div>
                      <DialogFooter className="pt-4 border-t mt-4">
                        <Button onClick={() => setModal({ open: false, type: null })} variant="outline" className={cn(
                          theme === 'light' ? 'text-white bg-red-600 hover:bg-red-700 border-red-600' : 'text-white bg-red-500 hover:bg-red-600 border-red-500'
                        )}>OK</Button>
                      </DialogFooter>
                    </DialogContent>
                  )}
                </Dialog>

                {/* PDF Download Confirmation Modal */}
                <Dialog open={pdfModalOpen} onOpenChange={setPdfModalOpen}>
                  <DialogContent className={cn(
                    theme === 'light' ? 'bg-white text-gray-800 shadow-lg rounded-lg' : 'bg-[#2A2A3E] text-gray-200 shadow-md rounded-md'
                  )}>
                    <DialogHeader>
                      <DialogTitle className={cn(theme === 'light' ? 'text-purple-700' : 'text-purple-300')}>PDF Downloaded</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 text-sm leading-relaxed">
                      Your invoice PDF has been downloaded.<br />
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Check your browser's downloads folder for the file.</li>
                        <li>You can now print, email, or share the PDF as needed.</li>
                      </ul>
                    </div>
                    <DialogFooter className="pt-4 border-t mt-4">
                      <Button onClick={() => setPdfModalOpen(false)} variant="outline" className={cn(
                        'w-full',
                        theme === 'light'
                          ? 'text-white bg-purple-700 hover:bg-purple-800 border-purple-700'
                          : 'text-white bg-purple-500 hover:bg-purple-400 border-purple-500'
                      )}>OK</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ) : (
            // No details message: Theme-aware
            <div className={cn(
              "text-center py-10",
              theme === 'light' ? 'text-gray-500' : 'text-purple-300'
            )}>
              No invoice details available or failed to load.
            </div>
          )}
        </CardContent>
      </Card>

      {/* DeleteConfirmDialog: Assuming it adapts or needs theme props */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteInvoice}
        title="Delete Invoice"
        description="Are you sure you want to delete this invoice? This action can be undone later."
        // Pass theme if needed: theme={theme}
      />
    </div>
  );
};

export default InvoiceDetails;